import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, ExternalLink, MapPin } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Google Logo SVG (respecting branding guidelines)
const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Star Rating Component
const StarRating = ({ rating, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClasses[size]} ${
            star <= rating 
              ? 'fill-yellow-400 text-yellow-400' 
              : star - 0.5 <= rating 
                ? 'fill-yellow-400/50 text-yellow-400' 
                : 'fill-gray-200 text-gray-200'
          }`}
        />
      ))}
    </div>
  );
};

// Individual Review Card
const ReviewCard = ({ review, index }) => {
  const [expanded, setExpanded] = useState(false);
  const maxLength = 150;
  const needsTruncation = review.text && review.text.length > maxLength;
  
  const displayText = needsTruncation && !expanded 
    ? review.text.substring(0, maxLength) + '...' 
    : review.text;

  return (
    <div
      className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#4A945D]/30 hover:shadow-lg transition-all"
      data-testid={`review-card-${index}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          {review.author_photo ? (
            <img 
              src={review.author_photo} 
              alt={review.author_name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#4A945D]/20 flex items-center justify-center text-[#4A945D] font-medium">
              {review.author_name?.charAt(0) || '?'}
            </div>
          )}
          <div>
            <h4 className="text-[#181B1F] font-medium">{review.author_name}</h4>
            <p className="text-gray-400 text-sm">{review.relative_time}</p>
          </div>
        </div>
        <StarRating rating={review.rating} size="sm" />
      </div>
      
      {/* Review Text */}
      <div className="relative">
        <Quote className="absolute -top-1 -left-1 w-6 h-6 text-[#4A945D]/20" />
        <p className="text-gray-600 text-sm leading-relaxed pl-4">
          {displayText || review.original_text}
        </p>
        {needsTruncation && (
          <button 
            onClick={() => setExpanded(!expanded)}
            className="text-[#4A945D] text-sm mt-2 hover:underline"
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>
    </div>
  );
};

// Main Google Reviews Component
const GoogleReviews = ({ 
  variant = 'full', // 'full', 'compact', 'homepage'
  maxReviews = 5,
  showTitle = true 
}) => {
  const [reviews, setReviews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/reviews`);
        setReviews(response.data);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setError('Unable to load reviews');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-white/5 rounded w-48"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map(i => (
            <div key={i} className="h-40 bg-white/5 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !reviews) {
    return null; // Silently fail - don't show error to users
  }

  // Filter to show only 4+ star reviews for homepage
  const displayReviews = variant === 'homepage' 
    ? reviews.reviews.filter(r => r.rating >= 4).slice(0, maxReviews)
    : reviews.reviews.slice(0, maxReviews);

  return (
    <div className="space-y-6" data-testid="google-reviews-section">
      {/* Header with Google branding */}
      {showTitle && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl text-white mb-2">
              {variant === 'homepage' ? 'What Our Customers Say' : 'Customer Reviews'}
            </h2>
            <div className="flex items-center gap-3">
              <GoogleLogo />
              <span className="text-gray-500 text-sm">Reviews on Google</span>
            </div>
          </div>
          
          {/* Rating Summary */}
          <div className="flex items-center gap-4 bg-white/5 px-4 py-3 rounded-sm">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{reviews.rating}</div>
              <StarRating rating={reviews.rating} size="md" />
            </div>
            <div className="border-l border-gray-200 pl-4">
              <div className="text-gray-500 text-sm">Based on</div>
              <div className="text-[#181B1F] font-medium">{reviews.total_reviews} reviews</div>
            </div>
          </div>
        </div>
      )}

      {/* Reviews Grid */}
      <div className={`grid gap-4 ${
        variant === 'compact' 
          ? 'grid-cols-1' 
          : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
      }`}>
        {displayReviews.map((review, index) => (
          <ReviewCard key={review.id || index} review={review} index={index} />
        ))}
      </div>

      {/* Footer with Google Link */}
      <div className="flex items-center justify-center gap-4 pt-4">
        <a
          href={reviews.google_maps_url || "https://maps.app.goo.gl/6HjX8WwmyzGy8NvR7"}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
          data-testid="view-all-reviews-link"
        >
          <GoogleLogo />
          <span>View all reviews on Google</span>
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Business Info */}
      {variant === 'full' && (
        <div className="flex items-center justify-center gap-2 text-gray-400 text-sm">
          <MapPin className="w-4 h-4" />
          <span>{reviews.address}</span>
        </div>
      )}
    </div>
  );
};

export default GoogleReviews;
